import { ArrowType, LineType } from '@ardoq/api-types';
import { GraphEdgeBase } from './GraphEdgeBase';
import { GraphItem, type GraphItemMetadata } from './GraphItem';
import SparkMD5 from 'spark-md5';
import { ReferenceTypeRepresentationData } from '../types';
import { DiffType } from '@ardoq/data-model';
import { getGraphItemLabel } from './getGraphItemLabel';

type MetaModelViewEdgeMetadata = {
  representationData: ReferenceTypeRepresentationData;
  hasFilters?: boolean;
};

type ConstructorArgs = {
  modelId: string;
  sourceGraphNodeId: string;
  targetGraphNodeId: string;
  sourceModelId: string;
  targetModelId: string;
  id?: string;
  metaData: MetaModelViewEdgeMetadata;
};
export class MetamodelViewGraphEdge extends GraphEdgeBase {
  static create(
    modelId: string,
    sourceId: string,
    targetId: string,
    graphNodeMap: Map<string, GraphItem>,
    id: string,
    metaData: MetaModelViewEdgeMetadata
  ): MetamodelViewGraphEdge {
    // TODO do we need new ids for the graph items?
    const sourceGraphNode = graphNodeMap.get(sourceId);
    const targetGraphNode = graphNodeMap.get(targetId);
    if (!sourceGraphNode || !targetGraphNode) {
      throw new Error(
        'Trying to create an edge without according source and/or target nodes'
      );
    }
    return new MetamodelViewGraphEdge({
      modelId: modelId,
      sourceGraphNodeId: sourceGraphNode.id,
      targetGraphNodeId: targetGraphNode.id,
      sourceModelId: sourceGraphNode.modelId,
      targetModelId: targetGraphNode.modelId,
      id,
      metaData,
    });
  }

  metaData: MetaModelViewEdgeMetadata;
  readonly source: string;
  readonly target: string;
  readonly sourceModelId: string;
  readonly targetModelId: string;

  private isSelected = false;
  private isInSelectedPath = false;
  private hasCollapsingRuleError = false;
  private isVirtual = false;

  private constructor({
    modelId,
    sourceGraphNodeId,
    targetGraphNodeId, // #region compatibility with the base class and the reused legacy code
    sourceModelId,
    targetModelId,
    id,
    metaData,
  }: ConstructorArgs) {
    const graphItemMetadata: GraphItemMetadata = {
      isComponent: false,
      isReference: false,
      isGroup: false,
    };
    super(modelId, graphItemMetadata, id);
    this.source = sourceGraphNodeId;
    this.target = targetGraphNodeId;
    this.sourceModelId = sourceModelId;
    this.targetModelId = targetModelId;

    this.metaData = metaData;

    this.hash = SparkMD5.hash(this.getRawHash());
  }

  // #region compatibility with the base class and the reused legacy code

  override getVisualDiffType(): DiffType {
    return DiffType.NONE;
  }
  override setLabel(label: string): void {
    this.metaData.representationData.displayLabel = label;
  }
  override getCSS(): string {
    return '';
  }
  override getItemLabels() {
    return undefined;
  }
  override setItemLabels(): void {}
  override hasURLFields(): boolean {
    return false;
  }
  // #endregion

  override getLabel() {
    return getGraphItemLabel(this.getRepresentationData().displayLabel);
  }

  setMetaData(metadata: MetaModelViewEdgeMetadata) {
    this.metaData = metadata;
  }
  getMetadata() {
    return this.metaData;
  }

  hasFilters() {
    return this.metaData.hasFilters || false;
  }

  getRepresentationData() {
    return this.metaData.representationData;
  }

  getColor() {
    return this.getRepresentationData().color;
  }

  getLineBeginning(): ArrowType {
    return this.getRepresentationData().lineBeginning ?? ArrowType.NONE;
  }

  getLineEnding(): ArrowType {
    return this.getRepresentationData().lineEnding;
  }

  getLine(): LineType {
    return this.getRepresentationData().line;
  }

  setIsSelected(isSelected: boolean) {
    this.isSelected = isSelected;
  }

  getIsSelected() {
    return this.isSelected;
  }

  setIsInSelectedPath(isSelected: boolean) {
    this.isInSelectedPath = isSelected;
  }

  getIsInSelectedPath() {
    return this.isInSelectedPath;
  }

  setHasCollapsingRuleError(hasError: boolean) {
    this.hasCollapsingRuleError = hasError;
  }

  getHasCollapsingRuleError() {
    return this.hasCollapsingRuleError;
  }

  setIsVirtual(isVirtual: boolean) {
    this.isVirtual = isVirtual;
  }

  getIsVirtual() {
    return this.isVirtual;
  }

  protected getRawHash() {
    return `${super.getRawHash()}${this.getLineBeginning()}${this.getLineEnding()}${
      this.sourceModelId
    }${this.targetModelId}`;
  }
}
